/**
 * Shared utility to validate that a search query contains only a single location/ZIP code.
 * Allows valid locations with commas (e.g., "Chicago, IL", "New York, NY, United States",
 * "Beverly Hills, CA 90210", "Paris, France") but blocks multiple separate locations
 * (e.g., "Chicago; Dallas", "Chicago | Dallas", "Chicago and Dallas", "90210 10001").
 */
export function validateSingleLocation(q: string): string | null {
  if (!q) {
    return "You must specify a Location (such as a City, Address, or ZIP).";
  }

  // Preserve newlines for validation but strip leading/trailing spaces
  const rawQuery = q.trim();

  // Reject newlines as they act as distinct list separators
  if (/\n|\r/.test(rawQuery)) {
    return "Multiple cities/locations detected. Please search for only one city or location at a time.";
  }

  // Normalize whitespace elsewhere (replace multiple spaces/tabs with a single space)
  const query = rawQuery.replace(/\s+/g, " ");

  // Reject separators like semicolon and pipe
  if (/[;|]/.test(query)) {
    return "Multiple cities/locations detected. Please search for only one city or location at a time.";
  }

  // Reject multiple ZIP codes
  const zipCodes = query.match(/\b\d{5}\b/g) || [];
  const uniqueZipCodes = Array.from(new Set(zipCodes));
  if (uniqueZipCodes.length > 1) {
    return "Multiple ZIP codes detected. Please enter only a single ZIP code or location.";
  }

  // Reject multiple locations separated by "and" or "or"
  const andParts = query.split(/\s+(?:and|or)\s+/i).map(p => p.trim()).filter(Boolean);
  if (andParts.length > 1) {
    const streetIndicators = [
      "st", "street", "ave", "avenue", "rd", "road", "blvd", "boulevard", 
      "ln", "lane", "dr", "drive", "cir", "circle", "way", "pl", "place", 
      "court", "ct", "broadway", "highway", "hwy", "crossing", "ctr", 
      "center", "loop", "pkwy", "parkway", "terrace", "ter", "run", "walk",
      "path", "trail", "tr", "pike"
    ];
    
    const isStreetOrIntersectionPart = (part: string): boolean => {
      const p = part.toLowerCase();
      if (/^\d+/.test(p)) return true;
      if (/^\d+(st|nd|rd|th)\b/.test(p)) return true;
      const words = p.split(/[\s,]+/);
      return words.some(w => streetIndicators.includes(w));
    };

    if (!isStreetOrIntersectionPart(andParts[0]) && !isStreetOrIntersectionPart(andParts[1])) {
      return "Multiple cities/locations detected. Please search for only one city or location at a time.";
    }
  }

  return null;
}
